<script lang="ts">
    import { onMount } from 'svelte'
    import { navigateTo } from 'yrv'
    import { NPS } from '../../modules/toolsApi/nps/nps/nps'
    import { NPSRepository } from '../../modules/toolsApi/nps/nps/nps.repository'
    import { NPSService } from '../../modules/toolsApi/nps/nps/nps.service'
    import type { CreateReviewDto } from '../../modules/toolsApi/nps/reviews/dto/create-review.dto'
    import { Review } from '../../modules/toolsApi/nps/reviews/review'
    import { ReviewType } from '../../modules/toolsApi/nps/reviews/reviews.enum'
    import { ReviewsRepository } from '../../modules/toolsApi/nps/reviews/reviews.repository'
    import { ReviewsService } from '../../modules/toolsApi/nps/reviews/reviews.service'
    import { decodeUuidFromLink } from './uuid-link-code'

    let npsR = new NPSRepository()
    let npsS = new NPSService(npsR)

    let reviewR = new ReviewsRepository()
    let reviewS = new ReviewsService(reviewR)

    let step = 1
    let rating = -1
    let text = ''
    let nps: NPS = new NPS()
    let review: Review = new Review()
    let companyId = null
    let personId = null
    let googleURL = 'https://search.google.com/local/writereview?placeid='

    onMount(async () => {
        await load()
    })

    async function load() {
        //todo
        const urlParams = new URLSearchParams(window.location.search)
        // os dois chegam em base64url desde que o WhatsApp passou a ler uuid como chave pix; link
        // antigo, com uuid cru, continua funcionando (ver decodeUuidFromLink)
        companyId = decodeUuidFromLink(urlParams.get('id'))
        personId = decodeUuidFromLink(urlParams.get('u'))
        if (!companyId || !personId) {
            step = 9
            return
        }
        window.api.users.statistics.tickPublicOnce(companyId, 'pub.nps.open')
        nps = await npsS.getNPSPublic(companyId)
    }

    async function send() {
        if (rating < 0) {
            alert('Escolha uma opção.')
            return
        }
        let dto: CreateReviewDto = {
            companyId: companyId,
            feedback: false,
            personId: personId,
            rating: rating,
            text: text,
            type: ReviewType.nps,
        }
        review = await reviewS.create(dto)
        window.api.users.statistics.tickPublic(companyId, 'pub.nps.create')
        if (rating === 10 && nps.googleOn && nps.googlePlaceId && !review.sendToGoogle) {
            step = 2 // google
        } else {
            step = 3 // thanks
        }
    }

    async function sendNo() {
        step = 3 // thanks
    }

    async function sendYes() {
        await reviewS.setSentToGoogle(review.reviewId)
        location.href = googleURL + nps.googlePlaceId
    }
</script>

<div class="root">
    {#if step === 1}
        <div class="form">
            <label for="">Em uma escala de 0 a 10, o quanto você indicaria nossa empresa para um amigo?</label>
            <select class="form-select" name="" id="" bind:value={rating}>
                <option value={-1}>Escolha</option>
                {#each [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as number}
                    <option value={number}>{number}</option>
                {/each}
            </select>
            {#if rating >= 0}
                {#if rating < 10}
                    <label for="">(opcional) O que podemos fazer para merecer de você uma nota maior no futuro?</label>
                    <textarea name="" id="" rows="4" bind:value={text} />
                {/if}
                <div class="d-flex justify-content-end">
                    <button on:click={send} class="btn btn-primary">&nbsp;&nbsp;&nbsp;Enviar&nbsp;&nbsp;&nbsp;</button>
                </div>
            {/if}
        </div>
    {/if}
    {#if step === 2}
        <label class="text-center mt-5 mb-4" for="">Você permite enviar essa avaliação ao Google?</label>
        <div class="text-center">
            <button on:click={sendNo} class="btn btn-secondary"
                >&nbsp;&nbsp;&nbsp;&nbsp;Não&nbsp;&nbsp;&nbsp;&nbsp;</button
            >&nbsp;&nbsp;&nbsp;&nbsp;
            <button on:click={sendYes} class="btn btn-primary"
                >&nbsp;&nbsp;&nbsp;&nbsp;Sim&nbsp;&nbsp;&nbsp;&nbsp;</button
            >
        </div>
    {/if}
    {#if step === 3}
        <div class="text-center">Muito obrigado por sua contribuição.</div>
    {/if}
    {#if step === 4}
        <div class="text-center">
            Você já respondeu este tópico recentemente.<br />
            Obrigado pelo interesse.
        </div>
    {/if}
    {#if step === 9}
        <div class="text-center">Indisponível no momento.</div>
    {/if}
</div>

<style>
    /* Tela pública: o visitante não tem tema escolhido, então o body fica sem classe de tema — e
       sem ela valem as variáveis do tema ESCURO, que é o padrão do app. Como esta página é branca,
       o campo saía com texto quase branco sobre branco. Aqui as variáveis voltam para os valores
       claros do design system (os mesmos de body.theme_light), e color-scheme deixa a lista do
       select nativo clara também. Vale para todo campo desenhado dentro do .root. */
    .root {
        --z-field-bg: #f7f8fa;
        --z-field-border: #dfe2e7;
        --z-field-text: #111111;
        --z-field-label: #6b7280;
        --z-field-focus: #2f765a;
        --z-field-focus-ring: #2f765a26;
        color-scheme: light;

        max-width: 970px;
        margin: 0 auto;
        margin-top: 64px;
        font-size: 20px;
        font-weight: 400;
        padding: 16px;
    }

    .form {
        display: flex;
        flex-direction: column;
    }
    label {
        margin-bottom: 8px;
    }
    select,
    textarea {
        margin-bottom: 16px;
    }
    /* o textarea não tem classe nenhuma e vinha com a cara crua do navegador, ao lado de um select
       com a pele do app: aqui os dois ficam iguais */
    textarea {
        background-color: var(--z-field-bg);
        color: var(--z-field-text);
        border: 1px solid var(--z-field-border);
        border-radius: var(--z-field-radius);
        padding: 10px 12px;
        font-family: inherit;
        font-size: 16px;
    }
    textarea:focus {
        outline: none;
        border-color: var(--z-field-focus);
        box-shadow: 0 0 0 3px var(--z-field-focus-ring);
    }
</style>
